import React, {useCallback, useContext, useEffect, useState} from 'react';
import {ChevronLeft, ChevronRight, Download, X} from 'lucide-react';
import styles from '../VLAOperators/VLAAdminShared.module.css';
import {
    downloadHexCapabilityRequestsExcel,
    fetchHexCapabilityRequests,
    fetchHexSessionChatLogs,
} from '../../../api/hexAdmin';
import {SwapMainAppContext} from '../../../context/SwapMainAppContext';
import {getApiErrorMessage, isSessionExpiredError} from '../../../utils/authErrors';

const STATUS_OPTIONS = [
    {value: 'pending', label: 'Pending'},
    {value: 'all', label: 'All'},
];

const REASON_LABELS = {
    weak_task_match: 'Uncertain match',
    zero_catalog_match: 'Zero catalog match',
    unknown_task_phrase: 'Unknown task phrase',
};

const formatDate = (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
};

const truncate = (text, max = 80) => {
    const value = String(text || '').trim();
    if (!value) return '—';
    return value.length > max ? `${value.slice(0, max - 1)}…` : value;
};

const ContactCell = ({row}) => {
    const email = String(row.user_email || '').trim();
    const telegram = String(row.telegram_id || '').trim();
    const discord = String(row.discord || '').trim();
    const hasContact = !!(email || telegram || discord);

    return (
        <div className={styles.userCell}>
            <div className={styles.userId}>
                ID: {row.user_id != null ? row.user_id : '—'}
            </div>
            {email ? (
                <div className={styles.userPrimary} title={email}>{email}</div>
            ) : null}
            {telegram ? (
                <div className={styles.userSecondary} title={telegram}>
                    TG: {telegram}
                </div>
            ) : null}
            {discord ? (
                <div className={styles.userSecondary} title={discord}>
                    Discord: {discord}
                </div>
            ) : null}
            {!hasContact && row.user_id == null ? (
                <div className={styles.userSecondary}>Anonymous session</div>
            ) : null}
            {!hasContact && row.user_id != null ? (
                <div className={styles.userSecondary}>No contact on file</div>
            ) : null}
        </div>
    );
};

const roleLabel = (role) => {
    const r = String(role || '').toLowerCase();
    if (r === 'user') return 'User';
    if (r === 'assistant') return 'Hex';
    if (r === 'tool_started' || r === 'tool_finished') return 'Tool';
    if (r === 'error') return 'Error';
    return role || 'Event';
};

const Pagination = ({page, totalPages, onChange}) => {
    if (totalPages <= 1) return null;
    return (
        <div className={styles.pagination}>
            <button
                type="button"
                className={styles.pageButton}
                onClick={() => onChange(page - 1)}
                disabled={page <= 1}
            >
                <ChevronLeft size={14} />
            </button>
            <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
            <button
                type="button"
                className={styles.pageButton}
                onClick={() => onChange(page + 1)}
                disabled={page >= totalPages}
            >
                <ChevronRight size={14} />
            </button>
        </div>
    );
};

const ChatTranscript = ({events, fallbackMessage}) => {
    if (!events || !events.length) {
        return (
            <div className={styles.chatFallback}>
                <p>No stored chat transcript for this session.</p>
                {fallbackMessage ? (
                    <div className={styles.chatBubbleUser}>
                        <div className={styles.chatRole}>User message at raise time</div>
                        <div className={styles.chatContent}>{fallbackMessage}</div>
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <div className={styles.chatTranscript}>
            {events.map((ev, idx) => {
                const role = String(ev.role || '').toLowerCase();
                const isUser = role === 'user';
                const isAssistant = role === 'assistant';
                const content = String(ev.content || '').trim();
                const toolLine = ev.tool_name
                    ? `${ev.tool_name}${ev.tool_result_summary ? `: ${ev.tool_result_summary}` : ''}`
                    : '';
                const body = content || toolLine;
                if (!body && role !== 'error') {
                    return null;
                }
                return (
                    <div
                        key={ev.log_id || `${ev.turn_number}-${ev.event_index}-${idx}`}
                        className={
                            isUser
                                ? styles.chatBubbleUser
                                : isAssistant
                                    ? styles.chatBubbleHex
                                    : styles.chatBubbleMeta
                        }
                    >
                        <div className={styles.chatRole}>
                            {roleLabel(ev.role)}
                            {Number.isFinite(ev.turn_number) && ev.turn_number > 0
                                ? ` · turn ${ev.turn_number}`
                                : ''}
                        </div>
                        <div className={styles.chatContent}>{body || '—'}</div>
                    </div>
                );
            })}
        </div>
    );
};

const VLAAdminHexCapabilityRequests = ({adminToken}) => {
    const {handleAdminSessionExpired} = useContext(SwapMainAppContext);
    const [statusFilter, setStatusFilter] = useState('pending');
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);
    const [chatModal, setChatModal] = useState(null);
    const [chatLoading, setChatLoading] = useState(false);
    const [chatError, setChatError] = useState(null);
    const [chatEvents, setChatEvents] = useState([]);

    const getToken = useCallback(
        () => adminToken || localStorage.getItem('prismax_admin_token'),
        [adminToken],
    );

    const fetchPage = useCallback(async (nextPage) => {
        const token = getToken();
        if (!token) {
            setError('Admin session expired. Please reconnect your wallet.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await fetchHexCapabilityRequests({
                token,
                status: statusFilter,
                page: nextPage,
            });
            setRows(Array.isArray(data.requests) ? data.requests : []);
            setPage(Number(data.pagination?.page || nextPage));
            setTotalPages(Number(data.pagination?.total_pages || 1));
            setTotalCount(Number(data.pagination?.total_count || 0));
        } catch (err) {
            if (isSessionExpiredError(err)) {
                handleAdminSessionExpired();
            }
            setError(getApiErrorMessage(err, 'Could not load Hex capability requests.'));
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [getToken, handleAdminSessionExpired, statusFilter]);

    useEffect(() => {
        void fetchPage(1);
    }, [statusFilter, fetchPage]);

    const openChatModal = useCallback(async (row) => {
        const token = getToken();
        if (!token) {
            setError('Admin session expired. Please reconnect your wallet.');
            return;
        }
        setChatModal(row);
        setChatLoading(true);
        setChatError(null);
        setChatEvents([]);
        try {
            const data = await fetchHexSessionChatLogs({
                token,
                sessionId: row.session_id,
            });
            setChatEvents(Array.isArray(data.events) ? data.events : []);
        } catch (err) {
            if (isSessionExpiredError(err)) {
                handleAdminSessionExpired();
            }
            setChatError(getApiErrorMessage(err, 'Could not load chat history.'));
        } finally {
            setChatLoading(false);
        }
    }, [getToken, handleAdminSessionExpired]);

    const closeChatModal = () => {
        setChatModal(null);
        setChatEvents([]);
        setChatError(null);
    };

    const handleDownloadExcel = useCallback(async () => {
        const token = getToken();
        if (!token) {
            setError('Admin session expired. Please reconnect your wallet.');
            return;
        }
        setDownloading(true);
        setError(null);
        try {
            await downloadHexCapabilityRequestsExcel({
                token,
                status: statusFilter,
            });
        } catch (err) {
            if (isSessionExpiredError(err)) {
                handleAdminSessionExpired();
            }
            setError(getApiErrorMessage(err, 'Could not download Excel export.'));
        } finally {
            setDownloading(false);
        }
    }, [getToken, handleAdminSessionExpired, statusFilter]);

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>VLA Admin — Hex Capability Requests</h1>
            <hr className={styles.divider} />

            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        User-raised catalog gaps
                        <span className={styles.count}>{totalCount}</span>
                    </h2>
                    <div className={styles.filterRow}>
                        {STATUS_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                className={
                                    statusFilter === opt.value
                                        ? styles.filterButtonActive
                                        : styles.filterButton
                                }
                                onClick={() => setStatusFilter(opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                        <button
                            type="button"
                            className={styles.collapseButton}
                            onClick={() => void handleDownloadExcel()}
                            disabled={loading || downloading || totalCount === 0}
                            style={{display: 'inline-flex', alignItems: 'center', gap: '6px'}}
                        >
                            <Download size={14} />
                            {downloading ? 'Downloading…' : 'Download Excel'}
                        </button>
                        <button
                            type="button"
                            className={styles.collapseButton}
                            onClick={() => void fetchPage(page)}
                            disabled={loading || downloading}
                        >
                            {loading ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>

                {error ? <p className={styles.errorMsg}>{error}</p> : null}

                {!loading && !error && rows.length === 0 ? (
                    <p className={styles.emptyMsg}>No capability requests yet.</p>
                ) : null}

                {rows.length > 0 ? (
                    <>
                        <div className={styles.tableWrapper}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Created</th>
                                        <th>Contact</th>
                                        <th>Reason</th>
                                        <th>User message</th>
                                        <th>Status</th>
                                        <th>Session</th>
                                        <th>Chat</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.request_id}>
                                            <td>{row.request_id}</td>
                                            <td>{formatDate(row.created_at)}</td>
                                            <td><ContactCell row={row} /></td>
                                            <td>
                                                <span className={styles.reasonBadge}>
                                                    {REASON_LABELS[row.reason_code] || row.reason_code || '—'}
                                                </span>
                                            </td>
                                            <td title={row.user_message}>{truncate(row.user_message, 72)}</td>
                                            <td>{row.status || 'pending'}</td>
                                            <td>
                                                <span className={styles.monoSmall} title={row.session_id}>
                                                    {truncate(row.session_id, 14)}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={styles.dashboardButton}
                                                    onClick={() => void openChatModal(row)}
                                                >
                                                    View chat
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination page={page} totalPages={totalPages} onChange={fetchPage} />
                    </>
                ) : null}
            </div>

            {chatModal ? (
                <div className={styles.dashboardModalOverlay} onClick={closeChatModal}>
                    <div
                        className={styles.dashboardModal}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.dashboardModalHeader}>
                            <div className={styles.dashboardModalTitle}>
                                Hex chat history
                                <span className={styles.dashboardModalEmail}>
                                    Request #{chatModal.request_id}
                                </span>
                            </div>
                            <button
                                type="button"
                                className={styles.dashboardModalClose}
                                onClick={closeChatModal}
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div className={styles.dashboardModalBody}>
                            <div className={styles.chatMetaRow}>
                                <span>Session: {chatModal.session_id}</span>
                                <span>User ID: {chatModal.user_id != null ? chatModal.user_id : '—'}</span>
                                {chatModal.user_email ? <span>Email: {chatModal.user_email}</span> : null}
                                {chatModal.telegram_id ? <span>TG: {chatModal.telegram_id}</span> : null}
                                {chatModal.discord ? <span>Discord: {chatModal.discord}</span> : null}
                            </div>
                            {chatLoading ? (
                                <p className={styles.emptyMsg}>Loading chat…</p>
                            ) : null}
                            {chatError ? <p className={styles.errorMsg}>{chatError}</p> : null}
                            {!chatLoading && !chatError ? (
                                <ChatTranscript
                                    events={chatEvents}
                                    fallbackMessage={chatModal.user_message}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default VLAAdminHexCapabilityRequests;
